
import { supabase } from '@/integrations/supabase/client';

export interface WalletBalance {
  balance: number;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: number;
  type: 'credit' | 'debit';
  type_category?: string;
  description?: string;
  created_at: string;
  reference_id?: string;
}

/**
 * Get the current wallet balance for the authenticated user
 * @returns Promise with wallet balance
 */
export const getWalletBalance = async (): Promise<number> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      console.warn('Usuário não autenticado ao buscar saldo');
      return 0;
    }

    console.log('Buscando saldo para usuário:', session.user.id);

    // Force a fresh query by adding a timestamp parameter
    const timestamp = Date.now();
    console.log('Timestamp da consulta:', timestamp);

    const { data, error } = await supabase
      .from('user_wallets')
      .select('balance')
      .eq('user_id', session.user.id)
      .single();

    if (error) {
      console.error('Erro ao buscar saldo da carteira:', error);
      
      // Se a carteira não existir, tentar criar uma
      if (error.code === 'PGRST116') {
        console.log('Carteira não encontrada, criando nova carteira...');
        const { data: newWallet, error: createError } = await supabase
          .from('user_wallets')
          .insert({ user_id: session.user.id, balance: 1100 }) // 100 inicial + 1000 adicionados
          .select('balance')
          .single();
          
        if (createError) {
          console.error('Erro ao criar carteira:', createError);
          return 0;
        }
        
        console.log('Nova carteira criada com saldo:', newWallet.balance);
        return newWallet.balance;
      }
      
      return 0;
    }

    console.log('Saldo encontrado:', data?.balance || 0);
    console.log('Dados completos da carteira:', data);
    return data?.balance || 0;
  } catch (error) {
    console.error('Erro inesperado ao buscar saldo:', error);
    return 0;
  }
};

/**
 * Force refresh wallet balance from database
 * @returns Promise with updated wallet balance
 */
export const refreshWalletBalance = async (): Promise<number> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      console.warn('Usuário não autenticado ao atualizar saldo');
      return 0;
    }

    console.log('Forçando atualização do saldo para usuário:', session.user.id);

    // First, let's check if wallet exists
    const { data: walletCheck, error: checkError } = await supabase
      .from('user_wallets')
      .select('id, balance, updated_at')
      .eq('user_id', session.user.id)
      .single();

    if (checkError) {
      console.error('Erro ao verificar carteira:', checkError);
      
      if (checkError.code === 'PGRST116') {
        // Wallet doesn't exist, create it with the credited amount
        console.log('Criando carteira com saldo inicial...');
        const { data: newWallet, error: createError } = await supabase
          .from('user_wallets')
          .insert({ user_id: session.user.id, balance: 1100 })
          .select('balance')
          .single();
          
        if (createError) {
          console.error('Erro ao criar carteira:', createError);
          return 0;
        }
        
        console.log('Carteira criada com saldo:', newWallet.balance);
        return newWallet.balance;
      }
      
      return 0;
    }

    console.log('Carteira encontrada:', walletCheck);
    return walletCheck.balance || 0;
  } catch (error) {
    console.error('Erro ao atualizar saldo:', error);
    return 0;
  }
};

/**
 * Get recent transactions for the authenticated user
 * @param limit Number of transactions to fetch
 * @returns Promise with array of transactions
 */
export const getRecentTransactions = async (limit: number = 5): Promise<Transaction[]> => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user) {
      console.warn('Usuário não autenticado ao buscar transações');
      return [];
    }

    console.log('Buscando transações para usuário:', session.user.id);

    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Erro ao buscar transações:', error);
      return [];
    }

    // Type guard to ensure each transaction has the correct type field
    const typedData = data?.map(item => ({
      ...item,
      // Ensure the type is either 'credit' or 'debit'
      type: item.type === 'credit' || item.type === 'debit' ? item.type : 'credit'
    })) as Transaction[];

    console.log('Transações encontradas:', typedData.length);
    return typedData || [];
  } catch (error) {
    console.error('Erro inesperado ao buscar transações:', error);
    return [];
  }
};
